if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.initThumbColumnNav();
      }

      get thumbList() {
        if (!this.elements.thumbnails) return null;
        return this.elements.thumbnails.querySelector('.thumbnail-list');
      }

      /* På desktop står miniaturerne i en lodret kolonne med et loft på seks
         synlige. Er der flere billeder end det, skal kolonnen kunne betjenes —
         Dawns egne pile duer ikke, de regner kun vandret. */
      isThumbColumnScrollable() {
        const list = this.thumbList;
        return !!list && list.scrollHeight > list.clientHeight + 1;
      }

      offsetWithinList(list, element) {
        return element.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
      }

      initThumbColumnNav() {
        const list = this.thumbList;
        if (!list) return;

        const up = this.elements.thumbnails.querySelector('.pdp-thumb-nav--up');
        const down = this.elements.thumbnails.querySelector('.pdp-thumb-nav--down');
        if (!up || !down) return;
        this.thumbNav = { up, down };

        up.addEventListener('click', () => this.stepThumbColumn(-1));
        down.addEventListener('click', () => this.stepThumbColumn(1));

        let queued = false;
        const sync = () => {
          if (queued) return;
          queued = true;
          requestAnimationFrame(() => {
            queued = false;
            this.updateThumbColumnNav();
          });
        };

        list.addEventListener('scroll', sync, { passive: true });
        window.addEventListener('resize', sync);
        // Miniaturerne kan skifte højde, når billederne er hentet.
        window.addEventListener('load', sync, { once: true });

        this.updateThumbColumnNav();
      }

      updateThumbColumnNav() {
        const list = this.thumbList;
        if (!list || !this.thumbNav) return;

        const scrollable = this.isThumbColumnScrollable();
        const atTop = list.scrollTop <= 1;
        const atEnd = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;

        // Skriv kun ved reelle ændringer: at vise eller skjule knapperne ændrer
        // kolonnens højde, og en blind skrivning ville kalde resize-lytteren
        // igen i en løkke.
        const hidden = !scrollable;
        const states = [
          [this.thumbNav.up, atTop],
          [this.thumbNav.down, atEnd],
        ];
        states.forEach(([button, disabled]) => {
          if (button.hidden !== hidden) button.hidden = hidden;
          if (button.disabled !== disabled) button.disabled = disabled;
        });
      }

      stepThumbColumn(direction) {
        const list = this.thumbList;
        if (!list) return;

        const item = list.querySelector('.thumbnail-list__item');
        const gap = parseFloat(window.getComputedStyle(list).rowGap) || 0;
        const itemHeight = item ? item.getBoundingClientRect().height + gap : list.clientHeight;
        if (!itemHeight) return;

        // Hele miniaturer ad gangen — ellers står man tilbage med en halv række.
        const perPage = Math.max(1, Math.floor((list.clientHeight + gap) / itemHeight));
        list.scrollTo({ top: list.scrollTop + direction * perPage * itemHeight, behavior: 'smooth' });
      }

      /* Klikker kunden på den nederste miniature, man kan se, rulles der ét
         trin ned, så det er tydeligt, at der er flere billeder. Før skulle man
         selv scrolle. Symmetrisk opad. */
      revealAroundThumbnail(thumbnail) {
        const list = this.thumbList;
        if (!list) return;

        const EDGE = 4; // tolerance for afrunding i layoutet
        const top = this.offsetWithinList(list, thumbnail);
        const bottom = top + thumbnail.getBoundingClientRect().height;
        const viewTop = list.scrollTop;
        const viewBottom = viewTop + list.clientHeight;

        if (top < viewTop - EDGE || bottom > viewBottom + EDGE) {
          list.scrollTo({ top, behavior: 'smooth' });
          return;
        }

        const next = thumbnail.nextElementSibling;
        if (next && bottom >= viewBottom - EDGE) {
          const nextBottom = this.offsetWithinList(list, next) + next.getBoundingClientRect().height;
          list.scrollTo({ top: nextBottom - list.clientHeight, behavior: 'smooth' });
          return;
        }

        const previous = thumbnail.previousElementSibling;
        if (previous && top <= viewTop + EDGE) {
          list.scrollTo({ top: this.offsetWithinList(list, previous), behavior: 'smooth' });
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        // På desktop står miniaturerne i en lodret kolonne ved siden af
        // billedet. Der skal der rulles på Y-aksen — isSlideVisible() og
        // offsetLeft nedenfor regner udelukkende vandret.
        if (this.isThumbColumnScrollable()) {
          this.revealAroundThumbnail(thumbnail);
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
